import { useCallback, useMemo } from 'react'
import isNil from 'lodash/isNil'

import type { ObjectUpdater } from '@app/types/common'

import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { omit } from '@app/utils/omit'
import { useAppDispatch } from '@app/utils/redux'

import { useMountEffect } from '@app/hooks/useMountEffect'

import { getDefaultFilter, getEffectiveFilter } from './actions'
import type { SavedAnnouncementsFilter } from './SavedAnnouncementsFilter'

export type AnnouncementsFilterManager = {
  filter: SavedAnnouncementsFilter
  effectiveFilter: SavedAnnouncementsFilter

  set(filter: SavedAnnouncementsFilter): void
  update: ObjectUpdater<SavedAnnouncementsFilter>
  reset(complete?: boolean): void
}

export const useSavedAnnouncementsFilterManager = (
  filter: SavedAnnouncementsFilter,
  onUpdate: (updater: (filter: SavedAnnouncementsFilter) => SavedAnnouncementsFilter) => void,
  now: Moment
): AnnouncementsFilterManager => {
  const dispatch = useAppDispatch()
  const defaultFilter = useMemo(() => dispatch(getDefaultFilter()), [dispatch])
  const effectiveFilter = useMemo(() => {
    return dispatch(getEffectiveFilter(filter))
  }, [dispatch, filter])

  const set = useCallback(
    (filter: SavedAnnouncementsFilter) => {
      onUpdate(() => filter)
    },
    [onUpdate]
  )

  const update = useCallback<ObjectUpdater<SavedAnnouncementsFilter>>(
    (key, value) => {
      onUpdate(filter => {
        if (isNil(value) || value === defaultFilter[key]) {
          return omit(filter, key)
        }

        let newFilter = { ...filter, [key]: value }
        if (key === 'min_kids' && typeof value === 'number' && !isNil(filter.max_kids) && value > filter.max_kids) {
          newFilter = omit(newFilter, 'max_kids')
        }
        if (key === 'max_kids' && typeof value === 'number' && !isNil(filter.min_kids) && value < filter.min_kids) {
          newFilter = omit(newFilter, 'min_kids')
        }
        if (key === 'min_hours' && typeof value === 'number' && filter.max_hours && value > filter.max_hours) {
          newFilter = omit(newFilter, 'max_hours')
        }
        if (key === 'max_hours' && typeof value === 'number' && filter.min_hours && value < filter.min_hours) {
          newFilter = omit(newFilter, 'min_hours')
        }
        if (key === 'min_date') {
          const mid = toMoment(value)
          const mad = toMoment(filter.max_date)
          if (mid && mad && mid.isAfter(mad, 'day')) {
            newFilter = omit(newFilter, 'max_date')
          }
        }
        if (key === 'max_date') {
          const mid = toMoment(filter.min_date)
          const mad = toMoment(value)
          if (mid && mad && mad.isBefore(mid, 'day')) {
            newFilter = omit(newFilter, 'min_date')
          }
        }
        if (key === 'youngest_age_months' && typeof value === 'number' && !isNil(filter.eldest_age_months) && value > filter.eldest_age_months) {
          newFilter = omit(newFilter, 'eldest_age_months')
        }
        if (key === 'eldest_age_months' && typeof value === 'number' && !isNil(filter.youngest_age_months) && value < filter.youngest_age_months) {
          newFilter = omit(newFilter, 'youngest_age_months')
        }

        return newFilter
      })
    },
    [defaultFilter, onUpdate]
  )

  const reset = useCallback(
    (complete = false) => {
      if (complete) {
        onUpdate(() => ({}))
        return
      }
      onUpdate(_filter => ({}))
    },
    [onUpdate]
  )

  useMountEffect(() => {
    const cleanup = (filter: SavedAnnouncementsFilter) => {
      {
        const date = toMoment(filter.min_date)
        if (date && date.isBefore(now, 'day')) {
          filter = omit(filter, 'min_date')
        }
      }
      {
        const date = toMoment(filter.max_date)
        if (date && date.isBefore(now, 'day')) {
          filter = omit(filter, 'max_date')
        }
      }
      return filter
    }

    const newFilter = cleanup(filter)
    if (newFilter !== filter) onUpdate(() => newFilter)
  })

  return {
    filter,
    effectiveFilter,
    set,
    update,
    reset,
  }
}

const toMoment = (date: unknown) => {
  if (typeof date !== 'string' || !date) return undefined
  return moment(date, 'YYYY-MM-DD')
}
